'use client'

import useLocalStorageState from '@components/hooks/useLocalStorageState'
import Input from '@components/primitives/Input'
import { Text } from '@components/primitives/Text'
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser'
import { NAME_PLACEHOLDER_VALUE } from '@lib/questionnaire/trialApplication/NAME_PLACEHOLDER_VALUE'
import {
  type AutoCompleteValues,
  autoCompleteLocalStorageKey,
} from '@modules/patientTrialApplications/autocomplete'
import { type ChangeEvent, useEffect } from 'react'
import { debounceTrackQuestionModified } from './trackQuestionModified'
import type { DemographicsCollectElementProps } from './types'

const debouncedEventTracking = debounceTrackQuestionModified('name')

export default function CollectName({
  formRef,
  patientProfileData: { name },
  questionConfig,
}: DemographicsCollectElementProps) {
  const [autoCompleteValues] = useLocalStorageState<AutoCompleteValues>(
    autoCompleteLocalStorageKey,
    {},
  )

  const defaultName = name[0] ?? autoCompleteValues.name ?? ''

  useEffect(() => {
    if (autoCompleteValues.name && !name[0]) {
      debouncedEventTracking(autoCompleteValues.name)
      setTimeout(() => formRef?.current?.dispatchEvent(new Event('input')), 50)
    }
  }, [autoCompleteValues.name, name[0]])

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newInputValue = event.target.value
    debouncedEventTracking(newInputValue)
  }

  return (
    <div className='flex w-full flex-col gap-2'>
      <Text
        className='text-neutral900'
        element={'p'}
        styleName='h3'
        value='What is your full legal name?'
      />
      <Text
        className='text-neutral900'
        element={'p'}
        styleName='p-small'
        value='Physicians need this to verify your identity.'
      />
      <Input
        autoComplete='name'
        autoFocus={true}
        className='h-14 rounded-md border-neutral200 bg-neutral50 text-neutral900'
        defaultValue={defaultName}
        icon={faUser}
        name={questionConfig.id}
        onChange={handleInputChange}
        placeholder={NAME_PLACEHOLDER_VALUE}
        required={!questionConfig.skippable}
      />
    </div>
  )
}
